import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Upload, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

const CompareOffer = () => {
  const [file, setFile] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState(null);
  const [comparisons, setComparisons] = useState([]);

  useEffect(() => {
    fetchComparisons();
  }, []);

  const fetchComparisons = async () => {
    try {
      const response = await api.getOfferComparisons();
      setComparisons(response.data.comparisons);
    } catch (error) {
      console.error('Failed to fetch comparisons:', error);
    }
  };

  const handleCompare = async (e) => {
    e.preventDefault();
    if (!file) return;

    setComparing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.compareOffer(formData);
      setResult(response.data);
      toast.success('Offert analyserad!');
      fetchComparisons();
    } catch (error) {
      console.error('Failed to compare offer:', error);
      toast.error('Kunde inte analysera offert');
    } finally {
      setComparing(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good':
        return <CheckCircle className="text-green-600" size={20} />;
      case 'overpriced':
        return <XCircle className="text-red-600" size={20} />;
      default:
        return <AlertTriangle className="text-yellow-600" size={20} />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'good':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Bra pris</span>;
      case 'overpriced':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Överprisat</span>;
      default:
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Normalt</span>;
    }
  };

  return (
    <div className="space-y-6" data-testid="compare-offer-page">
      <div>
        <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Work Sans' }}>Jämför Offert</h2>
        <p className="text-muted-foreground mt-1">Ladda upp en offert och jämför mot dina prislistor</p>
      </div>

      {/* Upload Form */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-6">
        <form onSubmit={handleCompare} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="offer-file">Välj offert (PDF eller Excel)</Label>
            <Input
              id="offer-file"
              type="file"
              accept=".pdf,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files[0])}
              required
              data-testid="offer-file-input"
            />
          </div>
          <Button
            type="submit"
            disabled={comparing || !file}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="compare-offer-btn"
          >
            {comparing ? 'Analyserar...' : 'Jämför offert'}
          </Button>
        </form>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <h3 className="text-xl font-semibold text-foreground mb-4">Analysresultat</h3>
          
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Totalt offertpris</p>
              <p className="text-2xl font-bold text-foreground">{result.total_offer_price?.toLocaleString('sv-SE')} kr</p>
            </div>
            {result.total_comparison_price && (
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">Jämförelsepris</p>
                <p className="text-2xl font-bold text-foreground">{result.total_comparison_price?.toLocaleString('sv-SE')} kr</p>
              </div>
            )}
            {result.total_savings !== null && (
              <div className={`rounded-lg p-4 ${
                result.total_savings > 0 ? 'bg-green-50' : result.total_savings < 0 ? 'bg-red-50' : 'bg-muted/50'
              }`}>
                <p className="text-sm text-muted-foreground">Besparing</p>
                <p className={`text-2xl font-bold ${
                  result.total_savings > 0 ? 'text-green-600' : result.total_savings < 0 ? 'text-red-600' : 'text-foreground'
                }`}>
                  {result.total_savings > 0 ? '+' : ''}{result.total_savings?.toLocaleString('sv-SE')} kr
                </p>
              </div>
            )}
          </div>

          {/* AI Recommendation */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-foreground mb-2">AI-rekommendation</h4>
            <p className="text-foreground/80">{result.ai_recommendation}</p>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Produkt</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Mängd</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Pris/enhet</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Totalpris</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Besparing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {result.items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(item.status)}
                        {getStatusBadge(item.status)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{item.product_name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{item.quantity} {item.unit}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{item.price_per_unit.toLocaleString('sv-SE')} kr</td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{item.total_price.toLocaleString('sv-SE')} kr</td>
                    <td className="px-4 py-3 text-sm">
                      {item.savings !== null ? (
                        <span className={item.savings > 0 ? 'text-green-600 font-medium' : item.savings < 0 ? 'text-red-600 font-medium' : 'text-muted-foreground'}>
                          {item.savings > 0 ? '+' : ''}{item.savings.toLocaleString('sv-SE')} kr
                        </span>
                      ) : (
                        <span className="text-muted-foreground/60">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Previous Comparisons */}
      {comparisons.length > 0 && (
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Tidigare jämförelser</h3>
          <div className="space-y-3">
            {comparisons.slice(0, 5).map((comp) => (
              <div key={comp.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium text-foreground">
                    {new Date(comp.created_at).toLocaleDateString('sv-SE')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Totalt: {comp.total_offer_price.toLocaleString('sv-SE')} kr
                    {comp.total_savings && (
                      <span className={comp.total_savings > 0 ? 'text-green-600 ml-2' : 'text-red-600 ml-2'}>
                        ({comp.total_savings > 0 ? '+' : ''}{comp.total_savings.toLocaleString('sv-SE')} kr)
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompareOffer;
